<template>
  <b-card class="customer-card h-100">
    <!-- Customer Header -->
    <div class="customer-header">
      <div class="customer-salutation">
        {{ customerEntry.salutation }}
      </div>
      <h4 class="customer-name">
        {{ customerEntry.firstName }} {{ customerEntry.lastName }}
      </h4>
      <div class="customer-meta">
        <div class="customer-id">
          <feather-icon
            icon="HashIcon"
            size="16"
          />
          Kunde #{{ customerEntry.id }}
        </div>
        <div class="customer-email">
          <feather-icon
            icon="MailIcon"
            size="16"
          />
          <a
            :href="`mailto:${customerEntry.email}`"
            class="email-link"
          >
            {{ customerEntry.email }}
          </a>
        </div>
      </div>
    </div>

    <!-- Contact Information -->
    <div class="contact-section">
      <div
        v-if="customerEntry.organizationName || customerEntry.company"
        class="info-row"
      >
        <div class="info-label">
          Unternehmen
        </div>
        <div class="info-value">
          {{ customerEntry.company || customerEntry.organizationName }}
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          Strasse
        </div>
        <div class="info-value">
          {{ customerEntry.street }} {{ customerEntry.streetNumber }}
        </div>
      </div>
      
      <div class="info-row">
        <div class="info-label">
          PLZ | Stadt
        </div>
        <div class="info-value">
          {{ customerEntry.zipCode }} {{ customerEntry.city }}
        </div>
      </div>
      
      <div
        v-if="customerEntry.country"
        class="info-row"
      >
        <div class="info-label">
          Land
        </div>
        <div class="info-value">
          {{ customerEntry.country }}
        </div>
      </div>

      <div
        v-if="customerEntry.phoneNumber"
        class="info-row"
      >
        <div class="info-label">
          Telefon
        </div>
        <div class="info-value">
          <a
            :href="`tel:${customerEntry.phoneNumber}`"
            class="phone-link"
          >
            {{ customerEntry.phoneNumber }}
          </a>
        </div>
      </div>

      <div
        v-if="customerEntry.mobileNumber"
        class="info-row"
      >
        <div class="info-label">
          Mobile
        </div>
        <div class="info-value">
          <a
            :href="`tel:${customerEntry.mobileNumber}`"
            class="phone-link"
          >
            {{ customerEntry.mobileNumber }}
          </a>
        </div>
      </div>

      <div
        v-if="customerEntry.faxNumber"
        class="info-row"
      >
        <div class="info-label">
          Fax
        </div>
        <div class="info-value">
          {{ customerEntry.faxNumber }}
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge } from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BButton,
    BRow,
    BCol,
    BBadge,
  },
  directives: {},
  props: {
    customerEntry: {
      type: Object,
      required: false,
    },
  },
  methods: {
    // warning
    deleteWarning() {
      this.$swal({
        title: 'Bist du sicher?',
        text: 'Alle Informationen zu dem Kunden (Kundendetails, Buchungen, Einnahmen, Aktivitäten, etc.) werden unwiederruflich gelöscht.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, löschen!',
        cancelButtonText: 'Abbrechen',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$swal({
            icon: 'success',
            title: 'Deleted!',
            text: 'Your file has been deleted.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-sweetalert.scss';

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.customer-card {
  border: 1px solid #e9ecef;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  .card-body {
    padding: 2rem;
  }
}

.customer-header {
  margin-bottom: 2rem;
  
  .customer-salutation {
    font-size: 0.8rem;
    color: #8a92b2;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  .customer-name {
    font-size: 1.75rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  .customer-meta {
    .customer-id {
      display: inline-flex;
      align-items: center;
      background: #f8f9fa;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-size: 0.875rem;
      color: #495057;
      margin-bottom: 0.75rem;
      margin-right: 1rem;
      
      .feather {
        margin-right: 0.5rem;
        color: #6c757d;
      }
    }
    
    .customer-email {
      display: flex;
      align-items: center;
      color: #6c757d;
      font-size: 1rem;
      margin-top: 0.25rem;
      
      .feather {
        margin-right: 0.5rem;
        color: #007bff;
      }
      
      .email-link {
        color: #007bff;
        text-decoration: none;
        font-weight: 500;
        
        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }
    }
  }
}

.contact-section {
  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.875rem 0;
    border-bottom: 1px solid #f1f3f4;
    
    &:last-child {
      border-bottom: none;
    }
    
    .info-label {
      font-size: 0.875rem;
      color: #8a92b2;
      font-weight: 500;
      min-width: 80px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .info-value {
      font-size: 1rem;
      color: #2c3e50;
      font-weight: 500;
      text-align: right;
      flex: 1;
      
      .phone-link {
        color: #007bff;
        text-decoration: none;
        
        &:hover {
          color: #0056b3;
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
